'use client';

import { useEffect, type ReactNode } from 'react';

const MAX_WHEEL_DISTANCE = 200;

// 입력창·팝업에서는 페이지 스크롤 제한을 적용하지 않는다.
function canScrollInside(target: EventTarget | null, delta: number) {
  let element = target instanceof Element ? target : null;
  while (
    element &&
    element !== document.body &&
    element !== document.documentElement
  ) {
    const { overflowY } = getComputedStyle(element);
    if (
      /(auto|scroll)/.test(overflowY) &&
      element.scrollHeight > element.clientHeight &&
      (delta < 0
        ? element.scrollTop > 0
        : element.scrollTop + element.clientHeight < element.scrollHeight)
    ) {
      return true;
    }
    element = element.parentElement;
  }
  return false;
}

export default function ScrollStepLimiter({
  children,
}: {
  children: ReactNode;
}) {
  useEffect(() => {
    const onWheel = (event: WheelEvent) => {
      const unit =
        event.deltaMode === 1
          ? 16
          : event.deltaMode === 2
            ? window.innerHeight
            : 1;
      const delta = event.deltaY * unit;
      if (Math.abs(delta) <= MAX_WHEEL_DISTANCE) return;

      if (
        event.defaultPrevented ||
        !event.cancelable ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        Math.abs(event.deltaY) <= Math.abs(event.deltaX) ||
        (event.target instanceof Element &&
          event.target.closest(
            'input, textarea, select, [contenteditable="true"], dialog, [role="dialog"], [aria-modal="true"]',
          )) ||
        ['hidden', 'clip'].includes(
          getComputedStyle(document.body).overflowY,
        ) ||
        ['hidden', 'clip'].includes(
          getComputedStyle(document.documentElement).overflowY,
        ) ||
        canScrollInside(event.target, event.deltaY)
      ) {
        return;
      }

      event.preventDefault();
      window.scrollBy({
        top: MAX_WHEEL_DISTANCE * Math.sign(delta),
        behavior: 'instant',
      });
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, []);

  return <>{children}</>;
}
